// Region / MAC / DME MAC follow the shipping State via StatesMaster
// (plans/state-derived-account-fields-plan.md). This mirrors the backend's
// accountStateDerivationService so the forms show exactly what a save will
// persist: a field is derived (and disabled) only when its own mapping exists
// for the state; otherwise it stays editable.

export const STATE_DERIVED_FIELDS = [
  "RegionID",
  "RegionName",
  "MedicareAdminContractor",
  "DMEMAC",
];

const text = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

// US state / territory abbreviations are 2 letters; allow 3 for safety. Full
// names or free text never match, so legacy rows stay unmapped (editable).
export const normalizeStateAbbreviation = (value) => {
  const normalized = text(value).toUpperCase();
  return /^[A-Z]{2,3}$/.test(normalized) ? normalized : "";
};

/** Index the `getstatesmaster` rows by abbreviation for O(1) lookups. */
export const buildStateLookup = (rows = [], regions = []) => {
  const regionNames = new Map(
    (regions || []).map((region) => [Number(region.value ?? region.ID), region.label ?? region.Region]),
  );
  const byAbbreviation = new Map();
  (rows || []).forEach((row) => {
    const key = normalizeStateAbbreviation(row?.Abbreviation);
    if (!key) return;
    const regionId = Number(row?.RegionID);
    byAbbreviation.set(key, {
      RegionID: Number.isInteger(regionId) && regionId > 0 ? regionId : null,
      MAC: text(row?.MAC),
      DME_MAC: text(row?.DME_MAC),
    });
  });
  return { byAbbreviation, regionNames };
};

/**
 * Values the backend will enforce for this state. Only keys with a mapping are
 * present; a RegionID whose Region is unknown to the picker is left out so the
 * UI never shows a disabled Region with no label.
 */
export const deriveFromState = (lookup, state) => {
  const row = lookup?.byAbbreviation?.get(normalizeStateAbbreviation(state));
  if (!row) return {};
  const derived = {};
  if (row.RegionID && lookup.regionNames.has(row.RegionID)) {
    derived.RegionID = row.RegionID;
    derived.RegionName = lookup.regionNames.get(row.RegionID);
  }
  if (row.MAC) derived.MedicareAdminContractor = row.MAC;
  if (row.DME_MAC) derived.DMEMAC = row.DME_MAC;
  return derived;
};

/** Which of the three form fields are locked for this state. */
export const getDerivedFieldFlags = (lookup, state) => {
  const derived = deriveFromState(lookup, state);
  return {
    RegionID: Object.prototype.hasOwnProperty.call(derived, "RegionID"),
    MedicareAdminContractor: Object.prototype.hasOwnProperty.call(
      derived,
      "MedicareAdminContractor",
    ),
    DMEMAC: Object.prototype.hasOwnProperty.call(derived, "DMEMAC"),
  };
};

/** Merge the derived values over form values; returns the same object when nothing changes. */
export const applyDerivedToValues = (lookup, values) => {
  const derived = deriveFromState(lookup, values?.State);
  const keys = Object.keys(derived);
  if (!keys.length) return values;
  const changed = keys.some((key) => (values?.[key] ?? null) !== derived[key]);
  return changed ? { ...values, ...derived } : values;
};

export const DERIVED_FIELD_HELP = "Auto-filled from the shipping state.";

export const unmappedFieldHelp = (label, state) => {
  const abbreviation = normalizeStateAbbreviation(state);
  return abbreviation
    ? `No ${label} configured for ${abbreviation} — enter it manually.`
    : "";
};
